import {z, type ZodRawShape, type ZodTypeAny} from 'zod';

interface CreateRenderInsertModeSchemaOptions<
  TBaseShape extends ZodRawShape,
  TInsertTargetElementIdSchema extends ZodTypeAny,
> {
  baseShape: TBaseShape;
  insertModeDescription: string;
  insertTargetElementIdSchema: TInsertTargetElementIdSchema;
}

export const DESTRUCTIVE_INSERT_MODES = ['replace', 'remove'] as const;
export const NON_DESTRUCTIVE_INSERT_MODES = [
  'before',
  'after',
  'append',
  'prepend',
] as const;

const ALL_INSERT_MODES = [
  ...NON_DESTRUCTIVE_INSERT_MODES,
  ...DESTRUCTIVE_INSERT_MODES,
] as const;

type DestructiveInsertMode = (typeof DESTRUCTIVE_INSERT_MODES)[number];

const destructiveInsertModes: ReadonlySet<string> = new Set(
  DESTRUCTIVE_INSERT_MODES
);

/**
 * Type guard for destructive insert modes (replace/remove).
 *
 * @param mode - The insertMode value to check.
 * @returns True if the mode requires an insertTargetElementId.
 */
const isDestructiveInsertMode = (mode: string): mode is DestructiveInsertMode =>
  destructiveInsertModes.has(mode);

/**
 * Creates the render argument schema with a runtime check that destructive
 * insertion modes require an insertTargetElementId.
 *
 * The schema is intentionally a flat z.object (rather than a discriminated
 * union) so the generated JSON schema has `type: "object"` at the root, which
 * is required by the Anthropic Messages API for tool input_schema. The
 * destructive-mode requirement is enforced via superRefine and described to
 * the model through insertModeDescription.
 *
 * @param options - The shared render fields plus manifest-specific descriptions.
 * @returns A z.object whose superRefine enforces destructive insert-mode rules.
 */
export const createRenderInsertModeSchema = <
  TBaseShape extends ZodRawShape,
  TInsertTargetElementIdSchema extends ZodTypeAny,
>({
  baseShape,
  insertModeDescription,
  insertTargetElementIdSchema,
}: CreateRenderInsertModeSchemaOptions<
  TBaseShape,
  TInsertTargetElementIdSchema
>) =>
  z
    .object({
      ...baseShape,
      insertMode: z.enum(ALL_INSERT_MODES).describe(insertModeDescription),
      insertTargetElementId: insertTargetElementIdSchema.optional(),
    })
    .superRefine((value, ctx) => {
      const {insertMode, insertTargetElementId} = value as {
        insertMode: (typeof ALL_INSERT_MODES)[number];
        insertTargetElementId?: unknown;
      };
      if (
        isDestructiveInsertMode(insertMode) &&
        insertTargetElementId === undefined
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['insertTargetElementId'],
          message: `insertTargetElementId is required when insertMode is "${insertMode}"`,
        });
      }
    });
